import { useCallback, useContext, useState } from "react";

import { API_ENDPOINTS } from "../api/constants";
import useWrite from "../api/useWrite";
import { AuthContext } from "../auth";

const LS_PREFIX = "walkthrough_dismissed_";

/**
 * Hook to manage the presentation boolean for a walkthrough, based on the field from the user's
 * completed walkthrough progress.
 *
 * @param walkthroughFieldName - name of the relevant walkthrough in user.walkthrough_progress.
 * @return {{walkthroughShowing: boolean, completeWalkthrough: function}}
 */
const useWalkthrough = walkthroughFieldName => {
  const { user, setUserData } = useContext(AuthContext);
  const lsKey = `${LS_PREFIX}${walkthroughFieldName}`;
  const isDismissed = () =>
    !!user?.walkthrough_progress?.[walkthroughFieldName] || !!localStorage.getItem(lsKey);
  const [walkthroughShowing, setWalkthroughShowing] = useState(!isDismissed());

  const walkthroughUpdate = useWrite(API_ENDPOINTS.WALKTHROUGH_PROGRESS(user?.id), true, {
    silent: true,
  });

  const completeWalkthrough = useCallback(() => {
    setWalkthroughShowing(false);
    localStorage.setItem(lsKey, "true");
    walkthroughUpdate.mutate(
      { [walkthroughFieldName]: true },
      {
        onSuccess: () => {
          setUserData();
        },
      }
    );
  }, [walkthroughFieldName, setUserData, walkthroughUpdate, lsKey]);

  return {
    walkthroughShowing: walkthroughShowing && !isDismissed(),
    completeWalkthrough,
  };
};

export default useWalkthrough;
